import { DestroyRef, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { GetCmsModulesRequest, GetGlobalCmsModulesRequest } from '@common/cms/shared';
import { AppRequestService, takeWhileAlive } from '@core/frontend-shared';
import { ProtocolDistributor } from '@core/shared/protocol';
import { BehaviorSubject, combineLatest, filter, map, of, share, switchMap, take, tap } from 'rxjs';
import { CMS_MODULE_OPTIONS } from '../cms-module-options.interface';
import { CMS_PROTOCOL_TOKEN } from '../tokens';
import * as i0 from "@angular/core";
import * as i1 from "@core/frontend-shared";
import * as i2 from "@angular/router";
import * as i3 from "@core/shared/protocol";
export let CmsModulesService = /*#__PURE__*/(() => {
  class CmsModulesService {
    constructor(api, router, options, protocol) {
      this.api = api;
      this.router = router;
      this.protocol = protocol;
      this.destroyRef = inject(DestroyRef);
      this.activePath$ = new BehaviorSubject(null);
      this.globalModules$ = of(null).pipe(tap(() => this.protocol.write('preloadGlobalModules start')), switchMap(() => {
        return this.api.sendRequest(GetGlobalCmsModulesRequest, {}).pipe(map(response => response.modules));
      }), tap(data => this.protocol.write('preloadGlobalModules received response', data)), share());
      const preloadCmsModulesForRoutes = options.preloadCmsModulesForRoutes || [];
      router.events.pipe(filter(event => event instanceof NavigationStart), takeWhileAlive(this.destroyRef)).subscribe(event => {
        this.activePath$.next(event.url);
        this.protocol.write('received NavigationStart event with url = ' + event.url);
      });
      if (preloadCmsModulesForRoutes.length) {
        this.activePath$.pipe(takeWhileAlive(this.destroyRef)).subscribe(path => {
          if (preloadCmsModulesForRoutes.includes(path)) {
            // preloading has become much simpler as the Requests will now use ClientCache mechanism.
            // so to preload, nothing but dispatching the request needs to be done.
            this.protocol.write('preloadModules for ' + path);
            this.api.sendRequest(GetCmsModulesRequest, {
              path
            }).subscribe();
          }
        });
      }
    }
    getModulesForPosition(position) {
      this.protocol.write('getModulesForPosition ' + position + ', activePath is: ' + this.activePath$.getValue());
      return combineLatest({
        positionModules: this.activePath$.pipe(switchMap(activePath => {
          return this.loadModulePositions(activePath, [position]);
        }), tap(() => this.protocol.write('getModulesForPosition: positionModules solved'))),
        globalModules: this.globalModules$.pipe(
        // tap((val)=>this.protocol.write('getModulesForPosition: globalModules -> ',val)),
        // filter out the initial value to ensure the module request has been finished
        filter(val => val !== null), tap(() => this.protocol.write('getModulesForPosition: globalModules solved')))
      }).pipe(take(1), map(({
        positionModules,
        globalModules
      }) => {
        const mergedModules = [...(globalModules[position] || []), ...(positionModules[position] || [])];
        return mergedModules;
      }), tap(data => {
        this.protocol.write('getModulesForPosition ' + position + ' completed.', data);
      }), takeWhileAlive(this.destroyRef));
    }
    loadModulePositions(path, positions) {
      return this.api.sendRequest(GetCmsModulesRequest, {
        path,
        positions
      }).pipe(map(response => {
        this.protocol.write('loadModulePositions received response for ' + path + ' and positions: [' + positions.join(',') + ']', response.modules);
        // when no modules are found for a position, API response may not include that position at all.
        // to prevent bugs, we make sure to add entries for all requested positions here.
        const populatedModulePositionsMap = {};
        for (const position of positions) {
          populatedModulePositionsMap[position] = response.modules[position] || [];
        }
        return populatedModulePositionsMap;
      }), takeWhileAlive(this.destroyRef));
    }
    static {
      this.ɵfac = function CmsModulesService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CmsModulesService)(i0.ɵɵinject(i1.AppRequestService), i0.ɵɵinject(i2.Router), i0.ɵɵinject(CMS_MODULE_OPTIONS), i0.ɵɵinject(CMS_PROTOCOL_TOKEN));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: CmsModulesService,
        factory: CmsModulesService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CmsModulesService;
})();